import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { MdKeyboardArrowDown, MdCloudUpload, MdClose } from "react-icons/md";
import { sendEmail } from "../../NetworkServices/services";
import { validateContactForm } from "../../utils/inputSanitizer";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };

const CONCERN_CATEGORIES = [
  "Account access",
  "Transactions & transfers",
  "Cards",
  "Loans & repayments",
  "Mobile / online banking",
  "Fraud or security issue",
  "Complaint",
  "Something else",
];

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.txt";

const labelStyle = {
  ...raleway,
  fontWeight: 500,
  fontSize: "13px",
  color: "#132A22",
  marginBottom: "8px",
  display: "block",
};

const baseFieldStyle = {
  ...raleway,
  width: "100%",
  height: "48px",
  padding: "0 16px",
  background: "#FFFFFF",
  border: "1px solid #E5E9E6",
  borderRadius: "10px",
  fontSize: "14px",
  color: "#132A22",
  outline: "none",
  boxSizing: "border-box",
};

const errorBorder = { borderColor: "#D14343" };

const ConcernForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      fullName: "",
      accountNumber: "",
      phone: "",
      email: "",
      category: "",
      details: "",
    },
    validate: (values) => {
      const errors = {};

      const validation = validateContactForm({
        name: values?.fullName,
        email: values?.email,
        body: values?.details,
      });
      if (validation?.errors?.name) errors.fullName = validation?.errors?.name;
      if (validation?.errors?.email) errors.email = validation?.errors?.email;
      if (validation?.errors?.body) errors.details = validation?.errors?.body;

      if (!values?.phone?.trim()) {
        errors.phone = "Phone number is required.";
      } else if (!/^[+\d][\d\s-]{6,}$/.test(values?.phone?.trim())) {
        errors.phone = "Enter a valid phone number.";
      }

      if (!values?.accountNumber?.trim()) {
        errors.accountNumber = "Account number is required.";
      } else if (!/^\d{10}$/.test(values?.accountNumber?.trim())) {
        errors.accountNumber = "Account number must be 10 digits.";
      }

      if (!values?.category) errors.category = "Please pick a category.";

      return errors;
    },
    onSubmit: (values) => {
      setIsLoading(true);

      const validation = validateContactForm({
        name: values?.fullName,
        email: values?.email,
        body: values?.details,
      });

      if (!validation?.isValid) {
        setIsLoading(false);
        Object.keys(validation?.errors || {})?.forEach((field) => {
          toast.error(validation?.errors?.[field]);
        });
        return;
      }

      const { name, email, body } = validation?.sanitizedData || {};
      const { category, phone, accountNumber } = values || {};

      const data = {
        userEmail: email,
        zikoraEmail: "customer.service@zikoramfb.com",
        subject: `Complaint — ${category} — ${name}`,
        body: `Complaint Submission\n\nName: ${name}\nEmail: ${email}\nPhone: ${phone}\nAccount Number: ${accountNumber}\nCategory: ${category}\n\nDetails:\n${body}${
          file?.name ? `\n\nAttachment: ${file?.name} (${(file?.size / 1024)?.toFixed(0)} KB)` : ""
        }`,
      };

      sendEmail(data)
        .then((val) => {
          setIsLoading(false);
          if (val?.status === 200) {
            toast.success(
              val?.data?.message ||
                "Thanks — your complaint is logged. We'll be in touch with a reference number shortly."
            );
            formik.resetForm();
            setFile(null);
          } else {
            toast.error(
              val?.data?.message || "Couldn't submit. Please try again."
            );
          }
        })
        .catch(() => {
          setIsLoading(false);
          toast.error("Couldn't submit. Please try again.");
        });
    },
  });

  const errFor = (n) =>
    formik?.touched?.[n] && formik?.errors?.[n] ? formik?.errors?.[n] : null;

  const handleFileChange = (e) => {
    const picked = e?.target?.files?.[0];
    if (!picked) return;
    if (picked?.size > MAX_FILE_BYTES) {
      toast.error("File is larger than 5MB. Please pick a smaller file.");
      return;
    }
    setFile(picked);
  };

  return (
    <>
      <style>{`
				.support-concern-input::placeholder { color: #9CA3AF; }
				.support-concern-row {
					display: flex;
					gap: clamp(28px, 4vw, 64px);
					align-items: flex-start;
				}
				.support-concern-text { flex: 0 0 34%; min-width: 0; position: sticky; top: 100px; }
				.support-concern-form { flex: 1 1 auto; min-width: 0; }
				.support-concern-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 20px;
				}
				.support-concern-upload {
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					gap: 6px;
					padding: 28px 16px;
					background: #FFFFFF;
					border: 1px dashed #D8DEDA;
					border-radius: 12px;
					cursor: pointer;
					transition: border-color 200ms ease, background 200ms ease;
					box-sizing: border-box;
				}
				.support-concern-upload:hover {
					border-color: #86C7A8;
					background: #F6FAF7;
				}
				@media (max-width: 900px) {
					.support-concern-row { flex-direction: column !important; }
					.support-concern-text {
						flex: none !important;
						width: 100% !important;
						position: static !important;
					}
					.support-concern-grid { grid-template-columns: 1fr !important; }
				}
			`}</style>

      <section
        id="support-concern"
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding:
            "clamp(56px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <div className="support-concern-row">
            {/* Left intro */}
            <div className="support-concern-text">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "clamp(16px, 1.6vw, 22px)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#295B4A",
                      display: "inline-block",
                    }}
                  />
                  <span
                    style={{
                      ...geistMono,
                      fontWeight: 500,
                      fontSize: "12px",
                      lineHeight: 1,
                      letterSpacing: "0.1em",
                      color: "#295B4A",
                      textTransform: "uppercase",
                    }}
                  >
                    Submit a complaint
                  </span>
                </div>

                <h2
                  style={{
                    ...instrumentSerif,
                    fontWeight: 400,
                    fontSize: "clamp(28px, 3.6vw, 52px)",
                    lineHeight: 1.1,
                    letterSpacing: "-0.01em",
                    color: "#132A22",
                    margin: 0,
                  }}
                >
                  We take every concern seriously.
                </h2>

                <p
                  style={{
                    ...raleway,
                    fontWeight: 400,
                    fontSize: "clamp(14px, 1.05vw, 15px)",
                    lineHeight: 1.7,
                    color: "#6B7B70",
                    margin: 0,
                    maxWidth: "360px",
                  }}
                >
                  We aim to resolve issues as quickly as possible. Fill in the
                  form and you'll get a reference number to track your case.
                </p>
              </div>
            </div>

            {/* Right form card */}
            <form
              onSubmit={formik.handleSubmit}
              className="support-concern-form"
              style={{
                background: "#FFFFFF",
                border: "1px solid #E5E9E6",
                borderRadius: "20px",
                padding: "clamp(24px, 3vw, 36px)",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                boxSizing: "border-box",
              }}
            >
              {/* Row 1: Full Name + Account Number */}
              <div className="support-concern-grid">
                <div>
                  <label style={labelStyle} htmlFor="support-fullName">
                    Full Name
                  </label>
                  <input
                    id="support-fullName"
                    name="fullName"
                    type="text"
                    className="support-concern-input"
                    placeholder="e.g Amanda Eze"
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...baseFieldStyle,
                      ...(errFor("fullName") && errorBorder),
                    }}
                  />
                  {errFor("fullName") && (
                    <span style={{ ...raleway, fontSize: "12px", color: "#D14343", marginTop: "6px", display: "block" }}>
                      {errFor("fullName")}
                    </span>
                  )}
                </div>

                <div>
                  <label style={labelStyle} htmlFor="support-accountNumber">
                    Account Number
                  </label>
                  <input
                    id="support-accountNumber"
                    name="accountNumber"
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    className="support-concern-input"
                    placeholder="10-digit account number"
                    value={formik.values.accountNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...baseFieldStyle,
                      ...(errFor("accountNumber") && errorBorder),
                    }}
                  />
                  {errFor("accountNumber") && (
                    <span style={{ ...raleway, fontSize: "12px", color: "#D14343", marginTop: "6px", display: "block" }}>
                      {errFor("accountNumber")}
                    </span>
                  )}
                </div>
              </div>

              {/* Row 2: Phone + Email */}
              <div className="support-concern-grid">
                <div>
                  <label style={labelStyle} htmlFor="support-phone">
                    Phone Number
                  </label>
                  <input
                    id="support-phone"
                    name="phone"
                    type="tel"
                    className="support-concern-input"
                    placeholder="+234..."
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...baseFieldStyle,
                      ...(errFor("phone") && errorBorder),
                    }}
                  />
                  {errFor("phone") && (
                    <span style={{ ...raleway, fontSize: "12px", color: "#D14343", marginTop: "6px", display: "block" }}>
                      {errFor("phone")}
                    </span>
                  )}
                </div>

                <div>
                  <label style={labelStyle} htmlFor="support-email">
                    Email Address
                  </label>
                  <input
                    id="support-email"
                    name="email"
                    type="email"
                    className="support-concern-input"
                    placeholder="you@email.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...baseFieldStyle,
                      ...(errFor("email") && errorBorder),
                    }}
                  />
                  {errFor("email") && (
                    <span style={{ ...raleway, fontSize: "12px", color: "#D14343", marginTop: "6px", display: "block" }}>
                      {errFor("email")}
                    </span>
                  )}
                </div>
              </div>

              {/* Complaint Category */}
              <div>
                <label style={labelStyle} htmlFor="support-category">
                  Complaint Category
                </label>
                <div style={{ position: "relative" }}>
                  <select
                    id="support-category"
                    name="category"
                    value={formik.values.category}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...baseFieldStyle,
                      ...(errFor("category") && errorBorder),
                      appearance: "none",
                      WebkitAppearance: "none",
                      cursor: "pointer",
                      paddingRight: "44px",
                      color: formik.values.category ? "#132A22" : "#9CA3AF",
                    }}
                  >
                    <option value="" disabled>
                      Select Category
                    </option>
                    {CONCERN_CATEGORIES?.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <MdKeyboardArrowDown
                    size={20}
                    style={{
                      position: "absolute",
                      right: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      color: "#9CA3AF",
                    }}
                  />
                </div>
                {errFor("category") && (
                  <span style={{ ...raleway, fontSize: "12px", color: "#D14343", marginTop: "6px", display: "block" }}>
                    {errFor("category")}
                  </span>
                )}
              </div>

              {/* Complaint Details */}
              <div>
                <label style={labelStyle} htmlFor="support-details">
                  Complaint Details
                </label>
                <textarea
                  id="support-details"
                  name="details"
                  rows={5}
                  className="support-concern-input"
                  placeholder="Tell us what happened..."
                  value={formik.values.details}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    ...baseFieldStyle,
                    height: "auto",
                    padding: "14px 16px",
                    resize: "vertical",
                    lineHeight: 1.55,
                    ...(errFor("details") && errorBorder),
                  }}
                />
                {errFor("details") && (
                  <span style={{ ...raleway, fontSize: "12px", color: "#D14343", marginTop: "6px", display: "block" }}>
                    {errFor("details")}
                  </span>
                )}
              </div>

              {/* Upload supporting document */}
              <div>
                <label style={labelStyle}>Upload Supporting Document</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />

                {!file && (
                  <div
                    className="support-concern-upload"
                    onClick={() => fileInputRef?.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e?.key === "Enter" || e?.key === " ") {
                        fileInputRef?.current?.click();
                      }
                    }}
                  >
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: "#EEF2EC",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#295B4A",
                        marginBottom: "4px",
                      }}
                    >
                      <MdCloudUpload size={18} />
                    </div>
                    <span
                      style={{
                        ...raleway,
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#295B4A",
                      }}
                    >
                      Click to upload
                    </span>
                    <span
                      style={{
                        ...raleway,
                        fontWeight: 400,
                        fontSize: "12px",
                        color: "#6B7B70",
                      }}
                    >
                      PDF, DOC, TXT (Max file size: 5MB)
                    </span>
                  </div>
                )}

                {file && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      padding: "14px 16px",
                      background: "#F6FAF7",
                      border: "1px solid #E5E9E6",
                      borderRadius: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          ...raleway,
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#132A22",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file?.name}
                      </span>
                      <span
                        style={{
                          ...geistMono,
                          fontWeight: 500,
                          fontSize: "11px",
                          color: "#6B7B70",
                        }}
                      >
                        {(file?.size / 1024)?.toFixed(0)} KB
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        if (fileInputRef?.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background: "#FFFFFF",
                        border: "1px solid #E5E9E6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        color: "#5E5E5E",
                        flexShrink: 0,
                      }}
                      aria-label="Remove file"
                    >
                      <MdClose size={16} />
                    </button>
                  </div>
                )}
              </div>

              {/* Privacy note */}
              <span
                style={{
                  ...raleway,
                  fontSize: "12px",
                  color: "#6B7B70",
                  lineHeight: 1.5,
                  marginTop: "4px",
                }}
              >
                By submitting, you agree to our complaint-handling and privacy
                policy.
              </span>

              {/* Submit row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    ...raleway,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "44px",
                    padding: "0 22px",
                    background: isLoading ? "#B8D4C5" : "#86C7A8",
                    border: "none",
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#132A22",
                    cursor: isLoading ? "default" : "pointer",
                    minWidth: "150px",
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={18} sx={{ color: "#132A22" }} />
                  ) : (
                    "Submit Complaint"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConcernForm;
